import React, { useEffect, useState } from "react";
import {
  Tab,
  Tabs,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { TabPanel } from "../common/TabPanel.js";
import StaffingLevelsTable from "../common/StaffingLevelsTable.js";
import "../common/StaffingLevelsTable.css";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import { fetchAuthSession } from "aws-amplify/auth";
import { Buffer } from "buffer";
import PulseLoader from "react-spinners/PulseLoader";

export default function StaffingLevels() {
  const [currentTab, setCurrentTab] = React.useState(1);
  const [data, setData] = React.useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dropdownSelection, setDropdownSelection] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const input = {
          FunctionName: "cco-dashboard-staffing-levels-api",
          InvocationType: "RequestResponse",
        };
        await fetchAuthSession().then(async ({ credentials }) => {
          const lambdaClient = new LambdaClient({
            region: "us-east-1",
            credentials: credentials,
          });

          const command = new InvokeCommand(input);

          const response = await lambdaClient.send(command);
          const responseData = JSON.parse(
            Buffer.from(response.Payload).toString()
          );
          setData(responseData);
          setLocations([
            ...new Set(responseData?.map((item) => item.workLocDesc)),
          ]);
          responseData?.length > 0 && setLoading(false);
        });
      } catch (e) {
        console.log(e);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const filtered =
      dropdownSelection?.length > 0
        ? data?.filter((item) => dropdownSelection?.includes(item.workLocDesc))
        : data;
    setFilteredData(filtered);
    setFilteredLocations([
      ...new Set(filtered?.map((item) => item.workLocDesc)),
    ]);
  }, [data, dropdownSelection]);

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const locationCounts = data?.reduce((acc, item) => {
    const { workLocDesc, jobCodeDesc } = item;
    const locationType = workLocDesc.includes("Remote") ? "Remote" : "Onsite";

    if (!acc[workLocDesc]) {
      acc[workLocDesc] = {
        count: { Onsite: 0, Remote: 0 },
        details: [],
        jobCodes: {},
      };
    }

    acc[workLocDesc].count[locationType] += 1;

    if (!acc[workLocDesc].jobCodes[jobCodeDesc]) {
      acc[workLocDesc].jobCodes[jobCodeDesc] = { Onsite: 0, Remote: 0 };
      acc[workLocDesc].jobCodes[jobCodeDesc][locationType] = 1;
      acc[workLocDesc].details.push({
        ...item,
        count: { Onsite: 0, Remote: 0 },
      });
    } else {
      acc[workLocDesc].jobCodes[jobCodeDesc][locationType] += 1;
    }

    acc[workLocDesc].details = acc[workLocDesc].details.map((detail) => {
      if (detail.jobCodeDesc === jobCodeDesc) {
        detail.count[locationType] += 1;
      }
      return detail;
    });

    return acc;
  }, {});

  const filteredLocationCounts = filteredLocations?.reduce((obj, key) => {
    if (locationCounts[key]) {
      obj[key] = locationCounts[key];
    }
    return obj;
  }, {});

  const handleDropdownChange = (event) => {
    const {
      target: { value },
    } = event;
    setDropdownSelection(typeof value === "string" ? value.split(",") : value);
  };

  const clearFilters = () => {
    setDropdownSelection([]);
  };

  return (
    <div className="dashboard">
      <Tabs value={currentTab} onChange={handleChange}>
        <Tab value={1} label="Total Staffing Levels" />
        <Tab value={2} label="Staffing Level by Location" />
      </Tabs>
      <TabPanel value={currentTab} index={1}>
        <h1>Total Staffing Levels</h1>
        <p>
          Comparison of Current Staffing Levels to the Staffing Level Goal by
          Fiscal Year.
        </p>
        <iframe
          src="https://app.powerbi.com/reportEmbed?reportId=87b474e0-a3c6-40f5-88eb-229aa7bcebd5&autoAuth=true&ctid=b699068d-c14b-454b-a0bc-10918cf075d3"
          title="Staffing Levels Dashboard"
          width="100%"
          height="775"
        ></iframe>
      </TabPanel>
      <TabPanel value={currentTab} index={2}>
        <h1>Staffing Level by Location</h1>
        <p>
          Number of onsite and remote employees for each site.
        </p>
        {loading && (
          <PulseLoader
            color={"#502E91"}
            loading={loading}
            cssOverride={{ textAlign: "center", paddingTop: "150px" }}
            aria-label="Loading Spinner"
          />
        )}
        {!loading && (
          <>
            <FormControl
              style={{ marginBottom: "10px", backgroundColor: "white" }}
            >
              <Select
                multiple
                value={dropdownSelection}
                onChange={handleDropdownChange}
                input={<OutlinedInput id="select-multiple-checkbox" />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? "Choose a location"
                    : selected.join(", ")
                }
                displayEmpty
                style={{
                  maxWidth: "300px",
                }}
              >
                {locations.map((location) => (
                  <MenuItem key={location} value={location}>
                    <Checkbox
                      checked={dropdownSelection.indexOf(location) > -1}
                    />
                    <ListItemText primary={location} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <button className="link" onClick={clearFilters}>
              Clear filters
            </button>
            <StaffingLevelsTable
              className="locationTable"
              data={filteredData}
              locationCounts={filteredLocationCounts}
            />
          </>
        )}
      </TabPanel>
    </div>
  );
}
